/**
 * The font choices a firm may set for its outbound **email bodies** (VALE-574).
 *
 * Mirrors `backend/src/common/utils/email-typography.util.ts`, which is the
 * authority: it resolves the stored value, renders the CSS, and stamps it onto
 * every outbound email on every send path. This file exists because the two
 * packages cannot import from each other — the same reason the account zod
 * schema mirrors the backend DTO. **Keep the two lists in step**; the backend
 * resolves an unrecognised value to its default, so a family offered here but
 * missing there would silently do nothing.
 *
 * Email only. A letter's typography belongs to the firm's own letterhead
 * document, which is why the letter-body markup vocabulary forbids `font-family`
 * outright.
 */

/** Families, each with the CSS stack the backend actually sends — used here for the preview. */
export const EMAIL_BODY_FONT_STACKS = {
  Arial: 'Arial, Helvetica, sans-serif',
  Calibri: "Calibri, Candara, 'Segoe UI', sans-serif",
  Helvetica: 'Helvetica, Arial, sans-serif',
  Georgia: "Georgia, 'Times New Roman', serif",
  'Times New Roman': "'Times New Roman', Times, serif"
} as const;

export type EmailBodyFontFamily = keyof typeof EMAIL_BODY_FONT_STACKS;

/**
 * `z.enum` needs a non-empty tuple of literals, so this is written out rather
 * than derived from `Object.keys` (which widens to `string[]`).
 */
export const EMAIL_BODY_FONT_FAMILIES = [
  'Arial',
  'Calibri',
  'Helvetica',
  'Georgia',
  'Times New Roman'
] as const satisfies readonly EmailBodyFontFamily[];

/**
 * Sizes in points — the unit a firm's Word precedents and signature are
 * specified in, and the unit firms ask in ("Arial 10").
 */
export const EMAIL_BODY_FONT_SIZES_PT = [10, 11, 12] as const;

export type EmailBodyFontSizePt = (typeof EMAIL_BODY_FONT_SIZES_PT)[number];

/** What a firm that has chosen nothing sends. Must match the backend default. */
export const DEFAULT_EMAIL_BODY_FONT_FAMILY: EmailBodyFontFamily = 'Arial';
export const DEFAULT_EMAIL_BODY_FONT_SIZE_PT: EmailBodyFontSizePt = 11;
